import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import CourseSidebar from '../courses/components/CourseSidebar';
import FooterTwo from '../../components/FooterTwo';
import { Styles } from './styles/applications.js';
import { APPLICATIONS_ENDPOINT } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const Applications = () => {
    const history = useHistory();
    const { user, loading: authLoading } = useAuth();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!authLoading && !user) {
            history.push(`${process.env.PUBLIC_URL || ''}/login`);
        }
    }, [authLoading, user, history]);

    useEffect(() => {
        const fetchApplications = async () => {
            if (!user?.email) {
                return;
            }

            setLoading(true);
            setError('');
            try {
                const response = await fetch(`${APPLICATIONS_ENDPOINT}?email=${encodeURIComponent(user.email)}`);
                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Unable to load applications.');
                }
                const payload = await response.json();
                setApplications(payload.applications || []);
            } catch (err) {
                setError(err.message || 'Unable to load applications.');
                setApplications([]);
            } finally {
                setLoading(false);
            }
        };

        fetchApplications();
    }, [user]);

    const tableRows = useMemo(() => applications.map((application) => {
        const course = application.course || {};
        const submittedOn = application.createdAt ? new Date(application.createdAt).toLocaleString() : 'N/A';
        const status = application.status || 'Submitted';
        return {
            id: application._id,
            program: application.courseProgramName || course.programName || 'Course Application',
            university: application.courseUniversityName || course.universityName || 'N/A',
            submittedOn,
            status,
            courseId: course._id || application.course,
            location: course.location || course.campus || '',
            message: application.message || ''
        };
    }), [applications]);

    return (
        <Styles>
            <div className="main-wrapper applications-page">
                <HeaderTwo />
                <BreadcrumbBox title="My Applications" />

                <section className="applications-area">
                    <Container>
                        <Row>
                            <Col lg="9" md="12">
                                {loading && (
                                    <div className="text-center my-4">
                                        <Spinner animation="border" role="status" />
                                    </div>
                                )}
                                {error && <Alert variant="danger">{error}</Alert>}
                                {!loading && !error && applications.length === 0 && (
                                    <Alert variant="info">No applications found.</Alert>
                                )}

                                {tableRows.map((row) => (
                                    <div className="application-box" key={row.id}>
                                        <Row>
                                            <Col xl="9" lg="8" md="12">
                                                <div className="application-content">
                                                    <div className="content-box">
                                                        <Row>
                                                            <Col md="8">
                                                                <div className="application-title">
                                                                    {row.courseId ? (
                                                                        <h6><Link to={`${process.env.PUBLIC_URL}/courses/${row.courseId}`}>{row.program}</Link></h6>
                                                                    ) : (
                                                                        <h6>{row.program}</h6>
                                                                    )}
                                                                </div>
                                                                <div className="application-info">
                                                                    <ul className="list-unstyled list-inline">
                                                                        <li className="list-inline-item"><i className="las la-university"></i>{row.university}</li>
                                                                        {row.location && (
                                                                            <li className="list-inline-item"><i className="las la-map-marker"></i>{row.location}</li>
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                                {row.message && (
                                                                    <div className="application-desc">
                                                                        <p>“{row.message}”</p>
                                                                    </div>
                                                                )}
                                                            </Col>
                                                            <Col md="4" className="text-center status-block">
                                                                <div className="submitted">{row.submittedOn}</div>
                                                                <span className="status-pill">{row.status}</span>
                                                                <div className="action-btn mt-2">
                                                                    {row.courseId ? (
                                                                        <Link to={`${process.env.PUBLIC_URL}/courses/${row.courseId}`}>View Course</Link>
                                                                    ) : (
                                                                        <span className="table-link disabled">No Course</span>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </Col>

                            <Col lg="3" md="0">
                                <CourseSidebar />
                            </Col>
                        </Row>
                    </Container>
                </section>

                <FooterTwo />
            </div>
        </Styles>
    );
};

export default Applications;
